import clsx from "clsx";
import { useLog } from "./QuickLog";
import { fmtShort } from "./ui";
import { useSession } from "../lib/session";

export type ReviewStage = { stage?: string | null; status?: string | null; prepared_on?: string | null; invited_on?: string | null; chased_on?: string | null;
  chase_count?: number | null; scheduled_at?: string | null; completed_on?: string | null; followup_sent_on?: string | null };

/** The steps of a client review, in order. Each one is an activity you log. */
export const REVIEW_STEPS: { stage: string; activity: string; label: string; date: (r: ReviewStage) => string | null | undefined }[] = [
  { stage: "Documents ready", activity: "Review documents prepared", label: "Documents prepared", date: (r) => r.prepared_on },
  { stage: "Times proposed", activity: "Review times proposed", label: "Times proposed", date: (r) => r.invited_on },
  { stage: "Booked", activity: "Review booked", label: "Booked", date: (r) => r.scheduled_at },
  { stage: "Held", activity: "Review held", label: "Review held", date: (r) => r.completed_on },
  { stage: "Follow-up sent", activity: "Review follow-up sent", label: "Follow-up sent", date: (r) => r.followup_sent_on },
];
const ORDER = ["Not started", "Documents ready", "Times proposed", "Reminder sent", "Booked", "Rescheduled", "Held", "Follow-up sent"];
export const stageIndex = (s?: string | null) => {
  const i = ORDER.indexOf(s ?? "Not started");
  return s === "Reminder sent" ? 1 : s === "Rescheduled" ? 2 : i <= 0 ? -1 : Math.min(REVIEW_STEPS.length - 1, REVIEW_STEPS.findIndex((x) => x.stage === s));
};

/** On a client: where the review stands, and a button to log each step. */
export function ReviewSteps({ person, r }: { person: { id: string; full_name: string }; r: ReviewStage | null }) {
  const log = useLog();
  const { can } = useSession();
  const edit = can("activities", "edit");
  const at = stageIndex(r?.stage);
  const go = (activity: string) => log({ area: "clients", person_id: person.id, person_name: person.full_name, activity_type: activity });
  return (
    <div className="rsteps">
      <ol>
        {REVIEW_STEPS.map((s, i) => {
          const d = r ? s.date(r) : null;
          const done = i <= at && r?.stage !== "Declined";
          return (
            <li key={s.stage} className={clsx(done && "done", i === at + 1 && "next")}>
              <span className="dot">{done ? "✓" : i + 1}</span>
              <span className="lbl">{s.label}</span>
              <span className="small muted">{done && d ? fmtShort(d) : i === at + 1 ? "Next" : ""}</span>
              {edit && !done && <button className="btn small" onClick={() => go(s.activity)}>Log</button>}
            </li>
          );
        })}
      </ol>
      {(r?.chase_count ?? 0) > 0 && <div className="small muted">Reminded {r!.chase_count} time{r!.chase_count === 1 ? "" : "s"}{r?.chased_on ? `, last ${fmtShort(r.chased_on)}` : ""}.</div>}
      {r?.stage === "Declined" && <div className="small muted">Client declined this review.</div>}
      {edit && <div className="toolbar" style={{ margin: "8px 0 0" }}>
        <span className="small muted">Also:</span>
        <button className="btn small ghost" onClick={() => go("Review reminder sent")}>Reminder sent (no reply yet)</button>
        <button className="btn small ghost" onClick={() => go("Review rescheduled")}>Rescheduled</button>
        <button className="btn small ghost" onClick={() => go("Review declined")}>Declined this year</button>
      </div>}
    </div>
  );
}
